import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormControl, InputLabel } from '@mui/material';
import moment from 'moment';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';

const StyledDiv = styled.div`
  .error-message {
    color: #bf1e2d;
    font-family: Whitney Medium;
    font-size: 13px;
    line-height: 1.3;
    margin-left: 0;
    margin-top: 0.5rem;
  }
`;

const StyledInputFormControl = styled(FormControl)`
  align-items: center;
  display: grid;
  min-width: 100%;

  svg {
    padding: 0 1rem;
  }

  &&& .MuiInputBase-root {
    border: 0;
    grid-column: span 2 / -1;
    grid-row: 1;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  &&& .MuiInputBase-root,
  &&& .MuiOutlinedInput {
    border: 1px solid #888888;
    border-radius: 0;
    font-family: 'Whitney Book';
    transition: none;
    width: 100%;

    &.Mui-focused,
    &:active,
    &:focus,
    &:target,
    &:hover {
      border-color: #006c9c;
      outline: 1px solid #006c9c;
      box-shadow: none;
    }
  }

  &&&&& input {
    border-bottom: none;
    margin: 0.5rem 0.75rem;

    &:focus {
      border-bottom: none;
      box-shadow: none;
    }
  }
`;

const StyledPicker = styled(DatePicker)`
  &:before,
  &:after,
  &:hover:not(.Mui-disabled):before {
    border-bottom: none;
    content: none;
    transition: none;
  }

  button:focus {
    background-color: rgba(0, 0, 0, 0.04);
  }

  svg {
    padding: 0;
  }
`;

const StyledLabel = styled(InputLabel)`
  &&&&&& {
    transform: none;
  }
`;

export const StyledDatePicker = ({
  maxDate,
  minDate,
  label,
  disabled,
  required,
  onChange,
  onAccept,
  value,
  name,
  maxDateErrorMessage,
  minDateErrorMessage,
  dateFormat,
  disablePast,
}) => {
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (minDate && new Date(value) < new Date(minDate)) {
      setError(true);
      setErrorMessage(
        minDateErrorMessage
          ? minDateErrorMessage
          : 'Date cannot be before ' +
              moment(minDate).format('MM/DD/YYYY') +
              '.'
      );
    } else if (maxDate && new Date(value) > new Date(maxDate)) {
      setError(true);
      setErrorMessage(
        maxDateErrorMessage
          ? maxDateErrorMessage
          : 'Date must be before ' + moment(maxDate).format('MM/DD/YYYY') + '.'
      );
    }
  }, [maxDateErrorMessage]);

  const handleChange = (date) => {
    if (minDate && date < new Date(minDate)) {
      setError(true);
      setErrorMessage(
        minDateErrorMessage
          ? minDateErrorMessage
          : 'Date cannot be before ' +
              moment(minDate).format('MM/DD/YYYY') +
              '.'
      );
    } else if (maxDate && date > new Date(maxDate)) {
      setError(true);
      setErrorMessage(
        maxDateErrorMessage
          ? maxDateErrorMessage
          : 'Date must be before ' + moment(maxDate).format('MM/DD/YYYY') + '.'
      );
    } else {
      setError(false);
    }

    onChange(moment(date).format('YYYY-MM-DD'));
  };

  const handleAccept = (date) => {
    if (minDate && date < new Date(minDate)) {
      setError(true);
      setErrorMessage(
        minDateErrorMessage
          ? minDateErrorMessage
          : 'Date cannot be before ' +
              moment(minDate).format('MM/DD/YYYY') +
              '.'
      );
    } else if (maxDate && date > new Date(maxDate)) {
      setError(true);
      setErrorMessage(
        maxDateErrorMessage
          ? maxDateErrorMessage
          : 'Date must be before ' + moment(maxDate).format('MM/DD/YYYY') + '.'
      );
    } else {
      setError(false);
    }

    onAccept(moment(date).format('YYYY-MM-DD'));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <StyledDiv>
        {label && (
          <StyledLabel className={disabled && 'disabled'}>
            {label}
            {!required && ' (optional)'}
          </StyledLabel>
        )}
        <StyledInputFormControl variant="outlined">
          <StyledPicker
            value={moment(value)}
            onChange={handleChange}
            onAccept={handleAccept}
            name={name}
            format={dateFormat || 'MM/DD/YYYY'}
            disablePast={disablePast}
            minDate={minDate && moment(minDate)}
            maxDate={maxDate && moment(maxDate)}
            slots={{
              openPickerIcon: () => (
                <FontAwesomeIcon
                  icon={faCalendar}
                  color="#006c9c"
                  className="calendar-icon"
                />
              ),
            }}
          />
        </StyledInputFormControl>
        {error && <p className="error-message">{errorMessage}</p>}
      </StyledDiv>
    </LocalizationProvider>
  );
};
